import { cn } from "@d12labs/utils";
import { Inbox, type LucideIcon } from "lucide-react";
import type * as React from "react";

/**
 * Centered empty / zero / error state. Icon in a hairline circle, title, body,
 * and an optional action pair (secondary left of primary, matching the system's
 * action order). `small` tightens the vertical rhythm for inline use inside a
 * card or panel.
 */
export function EmptyState({
	icon: Icon = Inbox,
	title,
	body,
	primary,
	secondary,
	small = false,
	className,
}: {
	icon?: LucideIcon;
	title: React.ReactNode;
	body?: React.ReactNode;
	primary?: React.ReactNode;
	secondary?: React.ReactNode;
	small?: boolean;
	className?: string;
}) {
	return (
		<div
			data-slot="empty-state"
			className={cn(
				"flex flex-col items-center justify-center text-center",
				small ? "gap-2 py-8" : "gap-3 py-16",
				className,
			)}
		>
			<div className="flex size-10 items-center justify-center rounded-full border border-hairline bg-surface-1 text-ink-subtle">
				<Icon className="size-4" />
			</div>
			<div className="max-w-sm">
				<h3 className="card-title text-foreground">{title}</h3>
				{body ? <p className="mt-1 text-body-sm text-ink-subtle">{body}</p> : null}
			</div>
			{primary || secondary ? (
				<div className="flex items-center gap-2 pt-1">
					{secondary}
					{primary}
				</div>
			) : null}
		</div>
	);
}
